(() => {
  const initialize = async (root) => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const i18n = JSON.parse(root.querySelector('[data-drone-i18n]').textContent);
    const text = (key, variables = {}) => Object.entries(variables).reduce((value, [name, replacement]) => value.replaceAll(`__${name.toUpperCase()}__`, String(replacement)), i18n[key]);
    const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: 'USD' });
    let catalog;
    try {
      const response = await fetch(root.dataset.catalogUrl, { credentials: 'omit' });
      if (!response.ok) throw new Error('catalog');
      catalog = await response.json();
      if (catalog.schemaVersion !== 1 || catalog.orderingEnabled !== false || catalog.products.length !== 13 || !catalog.copper || catalog.copper.defaultMm !== '0.3' || !catalog.copper.pricing['0.2'] || !catalog.copper.pricing['0.3']) throw new Error('catalog');
    } catch {
      root.querySelector('[data-load-error]').hidden = false;
      return;
    }
    const dialog = root.querySelector('[data-configurator]');
    const parallelInput = root.querySelector('[data-parallel]');
    const copperInput = root.querySelector('[data-copper]');
    const cellInput = root.querySelector('[data-cell]');
    const leadInput = root.querySelector('[data-lead]');
    const viewButtons = [...root.querySelectorAll('[data-view]')];
    catalog.cells.forEach((cell) => cellInput.add(new Option(cell.label, cell.key)));
    catalog.leads.forEach((lead) => leadInput.add(new Option(i18n.leadLabels[lead], lead)));
    let selectedProduct;
    let selectedView = 'face-a';
    let trigger;
    let previousOverflow;
    const imageFor = (series, parallel, view) => catalog.images[`${series}S${parallel}P`]?.[view] || null;
    const showImage = (container, url, series, parallel, view, lazy = false) => {
      container.replaceChildren();
      const pending = () => {
        const label = document.createElement('span');
        label.className = 'drone-preview__pending';
        label.textContent = i18n.drawingPending;
        container.replaceChildren(label);
      };
      if (!url) return pending();
      const img = document.createElement('img');
      img.width = 1200;
      img.height = 900;
      img.loading = lazy ? 'lazy' : 'eager';
      img.decoding = 'async';
      img.alt = text('imageAlt', { series, parallel, view: i18n.viewLabels[view] });
      img.addEventListener('error', pending, { once: true });
      img.src = url;
      container.append(img);
    };
    const variantFor = (product, parallel, cell, lead) => product.variants.find((variant) => variant.parallel === parallel && variant.cell === cell && variant.lead === lead);
    const updateGallery = () => {
      const parallel = Number(parallelInput.value);
      const series = selectedProduct.series;
      showImage(root.querySelector('[data-main-image]'), imageFor(series, parallel, selectedView), series, parallel, selectedView);
      viewButtons.forEach((button) => {
        button.disabled = !imageFor(series, parallel, button.dataset.view);
        button.setAttribute('aria-pressed', String(button.dataset.view === selectedView));
      });
      root.querySelector('[data-view-caption]').textContent = `${series}S${parallel}P · ${i18n.viewLabels[selectedView]}`;
    };
    const updateSelection = () => {
      const parallel = Number(parallelInput.value);
      const variant = variantFor(selectedProduct, parallel, cellInput.value, leadInput.value);
      const cell = catalog.cells.find((entry) => entry.key === cellInput.value);
      root.querySelector('[data-dialog-title]').textContent = text('dialogTitle', { series: selectedProduct.series, parallel });
      root.querySelector('[data-selection-summary]').textContent = text('selectionSummary', { series: selectedProduct.series, parallel, count: selectedProduct.series * parallel, cell: cell.label, copper: copperInput.value });
      const copperPricing = catalog.copper.pricing[copperInput.value];
      const copperDiscountCents = Math.max(copperPricing.minimumDiscountCents, copperPricing.discountCentsPerCell * selectedProduct.series * parallel);
      const displayedPrice = money.format((variant.proposedPriceCents - copperDiscountCents) / 100);
      root.querySelector('[data-dialog-price]').textContent = displayedPrice;
      const quoteVariables = { series: selectedProduct.series, parallel, cell: cell.label, lead: i18n.leadLabels[leadInput.value], price: displayedPrice, copper: copperInput.value };
      const quoteSubject = text('quoteSubject', quoteVariables);
      const quoteBody = text('quoteBody', quoteVariables);
      root.querySelector('[data-quote-request]').href = `mailto:sales@diy500amp.com?subject=${encodeURIComponent(quoteSubject)}&body=${encodeURIComponent(quoteBody)}`;
      root.querySelector('[data-stock-status]').textContent = variant.cellStockSufficientAtCheck ? i18n.stockSufficient : i18n.stockInsufficient;
      root.querySelector('[data-stock-date]').textContent = text('stockDate', { date: new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${catalog.stockCheckedAt}T12:00:00Z`)) });
      updateGallery();
    };
    catalog.products.forEach((product) => {
      const card = root.querySelector(`[data-series-card="${product.series}"]`);
      const defaultVariant = variantFor(product, 2, catalog.defaults.cell, catalog.defaults.lead);
      card.querySelector('[data-card-price]').textContent = money.format(defaultVariant.proposedPriceCents / 100);
      card.querySelector('[data-card-config]').textContent = text('cardConfig', { count: product.series * 2 });
      showImage(card.querySelector('[data-card-image]'), imageFor(product.series, 2, 'face-a'), product.series, 2, 'face-a', product.series > 4);
      const configure = card.querySelector('[data-configure]');
      configure.disabled = false;
      configure.addEventListener('click', () => {
        selectedProduct = product;
        trigger = configure;
        parallelInput.value = '2';
        copperInput.value = catalog.copper.defaultMm;
        cellInput.value = catalog.defaults.cell;
        leadInput.value = catalog.defaults.lead;
        selectedView = 'face-a';
        updateSelection();
        previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        dialog.showModal();
      });
    });
    [parallelInput, copperInput, cellInput, leadInput].forEach((input) => input.addEventListener('change', () => {
      if (input === parallelInput) selectedView = 'face-a';
      updateSelection();
    }));
    viewButtons.forEach((button) => button.addEventListener('click', () => {
      selectedView = button.dataset.view;
      updateGallery();
    }));
    root.querySelector('[data-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.body.style.overflow = previousOverflow || '';
      trigger?.focus();
    });
  };
  const start = (context = document) => context.querySelectorAll('[data-drone-preview]').forEach(initialize);
  start();
  document.addEventListener('shopify:section:load', (event) => start(event.target));
})();